// Live order chat panel — customer↔driver messaging bound to a single order.
// Reads /api/orders/{id}/messages, posts via /api/orders/{id}/messages,
// and listens for chat.message events over the shared order:{id} WebSocket topic.
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "@/api/client";
import useWebSocket from "@/hooks/useWebSocket";
import { useAuth } from "@/contexts/AuthContext";
import { MessageCircle, Send, Mic, PhoneCall, Loader2, Lock, Play, Pause, Square } from "lucide-react";
import { useVoice } from "@/contexts/VoiceCallContext";

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString("ar", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

function fmtDur(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/** Small inline voice-note player. Uses the authenticated /voice-notes/{id}
 * endpoint via a blob URL so we don't leak the audio through raw <audio src>
 * (which the browser fetches without the Authorization header). */
function VoiceMessagePlayer({ url, duration, isMe }) {
  const [playing, setPlaying] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  const toggle = async () => {
    if (playing) { audioRef.current?.pause(); setPlaying(false); return; }
    if (!blobUrl) {
      setLoading(true);
      try {
        const r = await api.get(url, { responseType: "blob" });
        const u = URL.createObjectURL(r.data);
        setBlobUrl(u);
        setTimeout(() => { audioRef.current && audioRef.current.play().catch(() => {}); }, 50);
      } catch { setLoading(false); return; }
      setLoading(false);
    } else {
      audioRef.current?.play().catch(() => {});
    }
    setPlaying(true);
  };

  return (
    <div className="flex items-center gap-2 min-w-[170px]" data-testid="voice-player">
      <button
        onClick={toggle}
        className={`w-9 h-9 rounded-full flex items-center justify-center ${isMe ? "bg-white/25" : "bg-primary-brand/15 text-primary-brand"}`}
        aria-label={playing ? "إيقاف" : "تشغيل"}
      >
        {loading ? <Loader2 size={16} className="animate-spin"/> : playing ? <Pause size={16}/> : <Play size={16}/>}
      </button>
      <div className={`text-xs font-bold tabular-nums ${isMe ? "text-white/90" : "text-[var(--text-primary)]"}`} dir="ltr">
        {fmtDur(duration)}
      </div>
      <audio ref={audioRef} src={blobUrl || undefined} onEnded={() => setPlaying(false)} preload="none"/>
    </div>
  );
}

function roleLabel(role, isMe) {
  if (isMe) return "أنا";
  if (role === "driver") return "السائق";
  if (role === "customer") return "الزبون";
  if (role === "admin") return "الإدارة";
  return role;
}

export default function OrderChat({ orderId, driverPhone, compact = false }) {
  const { user } = useAuth();
  const voice = useVoice();
  const [messages, setMessages] = useState([]);
  const [canWrite, setCanWrite] = useState(false);
  const [status, setStatus] = useState("");
  const [driverId, setDriverId] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const listRef = useRef(null);
  // Voice-note recorder state
  const [recording, setRecording] = useState(false);
  const [recSec, setRecSec] = useState(0);
  const recorderRef = useRef(null);
  const recChunksRef = useRef([]);
  const recStreamRef = useRef(null);
  const recStartedAtRef = useRef(0);
  const recTimerRef = useRef(null);
  const MAX_REC_SEC = 120;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}/messages`);
      setMessages(data.messages || []);
      setCanWrite(!!data.can_write);
      setStatus(data.status || "");
      setDriverId(data.driver_id || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "");
    } finally { setLoading(false); }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const { subscribe, status: wsStatus } = useWebSocket({
    onMessage: (msg) => {
      if (msg.event === "chat.message" && msg.data?.order_id === orderId) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.data.id)) return prev;
          return [...prev, msg.data];
        });
        // If a driver was just assigned, allow writing
        setCanWrite((cw) => cw || (msg.data.sender_role === "driver" || msg.data.sender_role === "customer"));
      }
      if (msg.event === "order.assigned" || msg.event === "order.status_changed") {
        // Refresh to update can_write & driver_id
        load();
      }
    },
  });

  useEffect(() => {
    if (wsStatus === "open" && orderId) subscribe([`order:${orderId}`]);
  }, [wsStatus, orderId, subscribe]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    const text = input.trim();
    if (!text || text.length > 500) return;
    setSending(true); setErr("");
    try {
      const { data } = await api.post(`/orders/${orderId}/messages`, { text });
      // Optimistic append — server also broadcasts via WS which we de-dupe.
      setMessages((prev) => prev.some((m) => m.id === data.id) ? prev : [...prev, data]);
      setInput("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "فشل إرسال الرسالة");
    } finally { setSending(false); }
  };

  // ---- Voice-note recorder (WhatsApp-style) ----
  const stopRecorderTracks = () => {
    if (recStreamRef.current) {
      recStreamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
      recStreamRef.current = null;
    }
    if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
  };

  const startRecord = async () => {
    if (recording) return;
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recStreamRef.current = stream;
      const opts = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? { mimeType: "audio/webm;codecs=opus", audioBitsPerSecond: 32000 }
        : {};
      const mr = new MediaRecorder(stream, opts);
      recChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) recChunksRef.current.push(e.data); };
      mr.start(500);
      recorderRef.current = mr;
      recStartedAtRef.current = Date.now();
      setRecSec(0);
      setRecording(true);
      recTimerRef.current = setInterval(() => {
        const dur = Math.floor((Date.now() - recStartedAtRef.current) / 1000);
        setRecSec(dur);
        if (dur >= MAX_REC_SEC) stopAndSend();
      }, 250);
    } catch {
      setErr("لم يتم منح صلاحية الميكروفون");
    }
  };

  const cancelRecord = () => {
    if (!recording) return;
    try { recorderRef.current?.stop(); } catch { /* ignore */ }
    recorderRef.current = null;
    recChunksRef.current = [];
    stopRecorderTracks();
    setRecording(false);
    setRecSec(0);
  };

  const stopAndSend = () => {
    if (!recording || !recorderRef.current) return;
    const mr = recorderRef.current;
    const dur = Math.floor((Date.now() - recStartedAtRef.current) / 1000);
    mr.onstop = async () => {
      stopRecorderTracks();
      setRecording(false);
      if (dur < 1 || !recChunksRef.current.length) { setRecSec(0); return; }
      const blob = new Blob(recChunksRef.current, { type: mr.mimeType || "audio/webm" });
      recChunksRef.current = [];
      setRecSec(0);
      const fd = new FormData();
      fd.append("audio", blob, "voice.webm");
      fd.append("duration_sec", String(dur));
      setSending(true);
      try {
        const { data } = await api.post(`/orders/${orderId}/voice-notes`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMessages((prev) => prev.some((m) => m.id === data.id) ? prev : [...prev, data]);
      } catch (e) {
        setErr(e?.response?.data?.detail || "تعذّر إرسال الرسالة الصوتية");
      } finally { setSending(false); }
    };
    try { mr.stop(); } catch { /* ignore */ }
  };

  // Ensure the mic is released if the component unmounts mid-recording.
  useEffect(() => () => { stopRecorderTracks(); }, []);

  const notEligible = useMemo(() => {
    if (!driverId) return "المحادثة تبدأ فور تعيين سائق لطلبك.";
    if (["pending", "preparing", "ready"].includes(status)) return "المحادثة تُفعّل بعد قبول السائق للطلب.";
    if (status === "delivered") return "المحادثة مغلقة — تم تسليم الطلب.";
    if (status === "cancelled") return "المحادثة مغلقة — الطلب مُلغى.";
    return null;
  }, [driverId, status]);

  if (loading) {
    return (
      <div className="bg-white rounded-3xl talabya-shadow-soft p-5 flex items-center gap-2 text-sm text-[var(--text-secondary)]">
        <Loader2 size={16} className="animate-spin"/> جاري تحميل المحادثة...
      </div>
    );
  }
  if (err && messages.length === 0 && !canWrite) {
    return null; // Not authorized — silently hide.
  }

  return (
    <div className="bg-white rounded-3xl talabya-shadow-soft overflow-hidden" data-testid="order-chat">
      <div
        className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between"
        style={{ background: "linear-gradient(135deg, #FFF7ED 0%, #FEF3E7 100%)" }}
      >
        <div className="flex items-center gap-2">
          <MessageCircle size={18} className="text-primary-brand"/>
          <div>
            <div className="font-black text-sm" style={{fontFamily:"Cairo"}}>محادثة الطلب</div>
            <div className="text-[10px] text-[var(--text-secondary)]">
              {canWrite ? "متصلة مباشرة" : "غير متاحة الآن"}
              {wsStatus === "open" && canWrite && <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"/>}
            </div>
          </div>
        </div>
        {["assigned", "picked_up"].includes(status) && user && (user.role === "customer" || user.role === "driver") && (
          <button
            onClick={() => voice.startOutgoing(orderId, {})}
            disabled={voice.state !== "idle" && voice.state !== "ended" && voice.state !== "error"}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-primary-brand/30 text-primary-brand text-xs font-black hover:bg-blue-50 disabled:opacity-50"
            data-testid="chat-call-btn"
          >
            <PhoneCall size={12}/> اتصال
          </button>
        )}
      </div>

      <div
        ref={listRef}
        className={`overflow-y-auto p-4 space-y-2.5 ${compact ? "max-h-[220px]" : "max-h-[320px]"} bg-[var(--surface-alt)]`}
        data-testid="chat-messages-list"
      >
        {messages.length === 0 ? (
          <div className="text-center text-sm text-[var(--text-secondary)] py-6">
            {notEligible ? (
              <div className="flex items-center justify-center gap-2">
                <Lock size={14}/> {notEligible}
              </div>
            ) : "ابدأ المحادثة برسالة نصية أو صوتية."}
          </div>
        ) : (
          messages.map((m) => {
            const isMe = m.sender_id === user?.id;
            const isSystem = m.sender_role === "admin";
            const isVoice = m.kind === "voice" && m.voice_url;
            return (
              <div key={m.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                <div
                  className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                    isMe
                      ? "bg-primary-brand text-white rounded-tr-sm"
                      : isSystem
                      ? "bg-blue-50 text-blue-800 border border-blue-100 rounded-tl-sm"
                      : "bg-white text-[var(--text-primary)] border border-[var(--border)] rounded-tl-sm"
                  }`}
                >
                  {!isMe && (
                    <div className={`text-[10px] font-black mb-0.5 ${isSystem ? "text-blue-700" : "text-primary-brand"}`}>
                      {roleLabel(m.sender_role, false)}{m.sender_name ? ` • ${m.sender_name}` : ""}
                    </div>
                  )}
                  {isVoice ? (
                    <VoiceMessagePlayer url={m.voice_url} duration={m.voice_duration_sec} isMe={isMe}/>
                  ) : (
                    <div className="leading-relaxed whitespace-pre-wrap break-words">{m.text}</div>
                  )}
                  <div className={`text-[9px] mt-1 ${isMe ? "text-white/75" : "text-[var(--text-secondary)]"}`}>{fmtTime(m.created_at)}</div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {canWrite && !notEligible ? (
        <div className="p-3 flex gap-2 border-t border-[var(--border)] bg-white items-center">
          {recording ? (
            <div className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-red-50 border border-red-200" data-testid="rec-active">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse-dot"/>
              <span className="text-sm font-black text-red-700 tabular-nums" dir="ltr">{fmtDur(recSec)}</span>
              <span className="text-xs text-red-600">جاري التسجيل...</span>
              <button
                onClick={cancelRecord}
                className="mr-auto px-3 py-1.5 rounded-lg bg-white border border-red-300 text-red-700 text-xs font-black"
                data-testid="rec-cancel"
              >إلغاء</button>
              <button
                onClick={stopAndSend}
                className="px-3 py-1.5 rounded-lg bg-primary-brand text-white text-xs font-black flex items-center gap-1"
                data-testid="rec-send"
              >
                <Send size={12}/> إرسال
              </button>
            </div>
          ) : (
            <>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                maxLength={500}
                className="flex-1 px-3 py-2.5 rounded-xl bg-[var(--surface-alt)] border border-transparent focus:border-primary-brand focus:bg-white focus:outline-none text-sm"
                placeholder="اكتب رسالة أو سجّل صوت..."
                data-testid="chat-input"
              />
              {input.trim() ? (
                <button
                  onClick={send}
                  disabled={sending}
                  className="px-3 py-2.5 rounded-xl bg-primary-brand text-white font-black disabled:opacity-50 flex items-center gap-1"
                  data-testid="chat-send"
                >
                  <Send size={14}/>
                </button>
              ) : (
                <button
                  onClick={startRecord}
                  className="w-11 h-11 rounded-xl bg-primary-brand text-white font-black flex items-center justify-center hover:opacity-90"
                  aria-label="سجّل رسالة صوتية"
                  data-testid="chat-record"
                >
                  <Mic size={18}/>
                </button>
              )}
            </>
          )}
        </div>
      ) : notEligible ? null : (
        <div className="p-3 text-xs text-[var(--text-secondary)] bg-[var(--surface-alt)] text-center border-t border-[var(--border)]">
          <Lock size={12} className="inline mr-1"/> غير مصرح لك بالكتابة في هذه المحادثة.
        </div>
      )}
      {err && <div className="px-4 pb-3 text-xs text-red-600">{err}</div>}
    </div>
  );
}
